/**
 * Custom hooks for collection sharing operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sharingService } from '../services/sharingService';
import { bottleService } from '../services/bottleService';
import { huntService } from '../services/huntService';
import { distilleryService } from '../services/distilleryService';

/**
 * Hook to fetch the list of users I have shared my collection with
 */
export function useMyViewers() {
  return useQuery({
    queryKey: ['sharing', 'viewers'],
    queryFn: sharingService.getMyViewers
  });
}

/**
 * Hook to fetch the list of collections shared with me
 */
export function useSharedWithMe() {
  return useQuery({
    queryKey: ['sharing', 'sharedWithMe'],
    queryFn: sharingService.getSharedWithMe
  });
}

/**
 * Hook to share my collection with another user by email
 */
export function useShareCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sharingService.shareWithUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sharing'] })
  });
}

/**
 * Hook to revoke a previously granted share
 */
export function useRevokeShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sharingService.revokeShare,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sharing'] })
  });
}

/**
 * Hook to fetch bottles for a specific shared user (read-only)
 * @param {string} userId - Owner's UUID
 * @param {Object} options - Query options
 */
export function useSharedBottles(userId, options = {}) {
  const { page = 1, limit = 20, sortBy = 'purchase_date', sortOrder = 'desc', filters = {} } = options;
  return useQuery({
    queryKey: ['shared-bottles', userId, { page, limit, sortBy, sortOrder, filters }],
    queryFn: () => bottleService.getBottlesForUser(userId, { page, limit, sortBy, sortOrder, filters }),
    enabled: !!userId,
    placeholderData: (previousData) => previousData
  });
}

/**
 * Hook to copy a shared distillery into the current user's collection.
 * Fetches the full distillery data (viewer has SELECT access via RLS),
 * then finds-or-creates it in the viewer's collection.
 */
export function useCopySharedDistillery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (distilleryId) => {
      const source = await distilleryService.getDistilleryById(distilleryId);
      const existing = await distilleryService.findByName(source.distillery_name);
      if (existing) return { distillery: existing, created: false };
      const { distillery_name, country, address, city, state_region, year_founded } = source;
      const created = await distilleryService.createDistillery({ distillery_name, country, address, city, state_region, year_founded });
      return { distillery: created, created: true };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['distilleries'] })
  });
}

/**
 * Hook to fetch hunt list for a specific shared user (read-only)
 * @param {string} userId - Owner's UUID
 * @param {Object} options - Query options
 */
export function useSharedHunts(userId, options = {}) {
  const { page = 1, limit = 20, filters = {} } = options;
  return useQuery({
    queryKey: ['shared-hunts', userId, { page, limit, filters }],
    queryFn: () => huntService.getHuntsForUser(userId, { page, limit, filters }),
    enabled: !!userId,
    placeholderData: (previousData) => previousData
  });
}
